import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Globe, ShieldCheck, Handshake } from "lucide-react";
import { BRAND } from "@/lib/brand";

const goTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="absolute inset-0 hero-gradient" aria-hidden="true" />
      <div className="absolute inset-0 grid-backdrop opacity-50" aria-hidden="true" />
      <div className="noise-overlay" aria-hidden="true" />

      <div className="relative container-narrow pt-16 sm:pt-24 lg:pt-28 pb-20 sm:pb-28 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
              className="inline-flex items-center gap-2 rounded-full border border-[rgba(244,234,222,0.18)] bg-[rgba(244,234,222,0.04)] px-3 py-1 text-xs font-mono uppercase tracking-[0.18em] text-[#EFE3D6]"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#E94B4B]" />
              International B2B Development & Brokerage
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05, ease: [0.2, 0.8, 0.2, 1] }}
              className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight leading-[0.95] text-[#F4EADE]"
            >
              You ask,<br />
              <span className="relative inline-block">
                <span className="text-[#F4EADE]">we </span>
                <span className="text-coral">develop.</span>
                <span className="absolute -bottom-2 left-0 h-1 w-2/3 bg-[#E94B4B]" aria-hidden="true" />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
              className="mt-6 max-w-xl text-base sm:text-lg text-[#EFE3D6]/85 leading-relaxed"
            >
              {BRAND.name} is your global B2B partner — we develop the platforms and
              integrations you need, and we broker the deals, supplier and partner
              relationships that move your business across borders.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
              className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <Button
                data-testid="hero-request-proposal-button"
                onClick={() => goTo("#contact")}
                className="h-12 px-6 text-sm sm:text-base bg-[#6B1F47] hover:bg-[#7A1F4A] text-[#F4EADE] btn-lift focus-ring shadow-[0_0_0_1px_rgba(233,75,75,0.0)] hover:shadow-[0_0_0_1px_rgba(233,75,75,0.35)]"
              >
                Request a proposal <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                data-testid="hero-explore-services-button"
                onClick={() => goTo("#services")}
                variant="outline"
                className="h-12 px-6 text-sm sm:text-base bg-transparent border-[rgba(244,234,222,0.22)] text-[#F4EADE] hover:bg-[rgba(244,234,222,0.06)] hover:border-[rgba(244,234,222,0.35)] btn-lift focus-ring"
              >
                Explore services
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 grid grid-cols-3 max-w-md gap-3 text-xs sm:text-sm text-[#B9AFA6]"
            >
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-coral" /> Global ready
              </div>
              <div className="flex items-center gap-2">
                <Handshake className="h-4 w-4 text-coral" /> Trusted broker
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-coral" /> Built for scale
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
              className="relative aspect-square w-full max-w-md mx-auto"
            >
              <div className="absolute -inset-6 rounded-[28px] border border-[rgba(244,234,222,0.10)] clip-diagonal pointer-events-none" />
              <div className="relative h-full w-full rounded-[24px] border border-[rgba(244,234,222,0.14)] bg-[#111015] overflow-hidden clip-diagonal">
                <div
                  className="absolute inset-0 opacity-80"
                  style={{
                    backgroundImage:
                      "url('https://images.unsplash.com/photo-1665211097487-2d9d022084ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0B0A0C]/70 via-[#0B0A0C]/30 to-[#6B1F47]/55" />
                <div className="absolute inset-0 grid-backdrop opacity-40" />
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                  <img
                    src={BRAND.logoUrl}
                    alt="GREINCH WW LLC"
                    className="h-44 sm:h-52 w-auto object-contain drop-shadow-[0_18px_40px_rgba(233,75,75,0.35)]"
                  />
                  <div className="mt-4 text-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#EFE3D6]/80">
                      Global Commerce Partner
                    </div>
                    <div className="mt-1 font-display text-lg sm:text-xl text-[#F4EADE] tracking-tight">
                      Developed. Brokered. Delivered.
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -right-4 -top-4 h-16 w-16 border border-[#E94B4B]/40 rotate-45" aria-hidden="true" />
              <div className="absolute -left-3 -bottom-3 h-10 w-10 bg-[#6B1F47]/30" aria-hidden="true" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
